import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Users,
  Target,
  AlertCircle,
  CheckCircle2,
  Zap,
  Info,
  User,
  UserCheck,
  AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { CapacityBar } from '@/components/events/CapacityBar';
import { EmptyState } from '@/components/ui/empty-state';
import { SectionLabel } from '@/components/ui/section-label';
import { PartnerInfoDisplay } from '@/components/events/PartnerInfoDisplay';
import { EntryPreferencesDisplay } from '@/components/events/EntryPreferencesDisplay';
import { EditPreferencesDialog } from '@/components/events/EditPreferencesDialog';
import {
  detectConflictsForEvent,
  type EntryWithPreferences,
} from '@/utils/entryPreferences';
import { RegistrationModal } from '@/components/events/RegistrationModal';
import { PayNowDialog } from '@/components/events/PayNowDialog';
import { BulkPayDialog } from '@/components/events/BulkPayDialog';
import { CancelRegistrationDialog } from '@/components/events/CancelRegistrationDialog';
import { PublicDrawResultsCard } from '@/components/events/PublicDrawResultsCard';
import { PublicEventResultsCard } from '@/components/events/PublicEventResultsCard';
import { EventPrizeBreakdownCard } from '@/components/events/EventPrizeBreakdownCard';
import { useEvents } from '@/api/generated_hooks/generated-events-hooks';
import { PerformancesClient } from '@/api/generated_clients/generated-performances-api-client';
import type { PerformancesRow } from '@/types/generated_schemas/generated-performances-schema';
import { useJackpotDetail, useEventRulesets } from '@/api/extensions/useJackpotOps';
import {
  useMyRegistrations,
  useEventCapacity,
  useCancelTeamRegistration,
  useRegistrationPolicy,
  useRespondToPartner,
  useOutstandingBalance,
  useBuddyGroupRegistrationStatus,
} from '@/api/extensions/useRegistrationOps';
import { useMyUpcomingEvents } from '@/api/extensions/useDashboardData';
import { useStripeFeeConfig } from '@/api/extensions/useStripeFeeConfig';
import { calculateTransactionFee } from '@/utils/stripe-fee-calculator';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { useMyBuddyGroup } from '@/api/extensions/useBuddyGroupOps';
import { useToast } from '@/contexts/ToastContext';
import { useOrganizations } from '@/hooks/useOrganizations';
import { formatCurrency, formatDate, formatDateRange } from '@/utils/formatters';
import { formatInTimezone } from '@/utils/timezone';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { describeBalancedSplit } from '@/utils/balancedPositions';
import { isFullProfileComplete } from '@/utils/profileCompleteness';

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const { isAuthenticated, profile } = useAuthStore();
  const myProfileId = profile?.id ?? null;

  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [payNowRegistrationId, setPayNowRegistrationId] = useState<string | null>(null);
  // Pay-Now-on-verifying-ACH guard. When an entry's current payment is a bank
  // (ACH) debit still awaiting microdeposit verification, opening a fresh
  // checkout SUPERSEDES it (the backend cancels the unverified PaymentIntent on
  // completion). We interpose a confirmation so the user doesn't abandon a
  // pending verification by accident. Mirrors the Funds/Payments tab guard.
  const [verifyWarn, setVerifyWarn] = useState<
    { id: string; eventName: string | null } | null
  >(null);
  // Bulk pay basket — used when the user lands on this page via
  // ?action=pay and they have MULTIPLE pending_payment registrations on
  // the same event (e.g. a buddy-group team_primary covering both sides
  // + a solo entry). One Stripe charge settles the lot so the total
  // matches what the user expected to pay — not just the first row's
  // amount. Snapshot of ids is held so a row landing mid-flight can't
  // change what's being paid.
  const [bulkPayIds, setBulkPayIds] = useState<string[] | null>(null);
  // "Edit preferences" target. Captured at click time so the dialog
  // gets the current registration's picks + rodeo context. Hidden on
  // buddy-group rows (locked at the group level).
  const [editPrefsTarget, setEditPrefsTarget] = useState<{
    registrationId: string;
    currentFirst: string | null;
    currentSecond: string | null;
  } | null>(null);

  // Data fetching
  const { data: event, isLoading: loadingEvent } = useEvents(eventId!);
  // include_buddy_group: when the viewer is the primary cardholder of any
  // buddy group, also returns teammate rows from those groups (tagged
  // `is_primary_managed_view`) so the primary can manage the entire
  // roster. Non-primaries get only their own rows.
  const { data: myRegs, isLoading: loadingMyRegs } = useMyRegistrations({ event_id: eventId, include_buddy_group: true });
  // Outstanding balance is the source of truth for "what does this user owe
  // RIGHT NOW" — it knows about supplemental amounts (post-rejection
  // surcharge on already-paid registrations) that aren't visible from the
  // registration row alone. Used to surface Pay Now on confirmed rows that
  // carry a partial balance and to compute the cancel-refund estimate
  // against the user's actual capture history (not the inflated
  // payment_amount column).
  const { data: outstandingBalance } = useOutstandingBalance();
  const owedByRegistrationId = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of outstandingBalance?.unpaid_events ?? []) {
      if (e.registration_id) map.set(e.registration_id, e.amount_owed);
    }
    return map;
  }, [outstandingBalance]);
  const { data: capacity } = useEventCapacity(eventId!);
  const { data: policyData } = useRegistrationPolicy(eventId!);
  const { data: eventRulesetsData } = useEventRulesets(eventId!);
  // Platform Stripe fee config — fetched up here (not next to the fee
  // derivation below) so the hook order stays stable across renders. The
  // earlier loading/error early-returns guard the JSX, but they must NOT
  // sit between hook calls; placing useStripeFeeConfig after them caused
  // React error #310 ("Rendered more hooks than during the previous
  // render") and white-screened the page on Kuna rodeo events.
  const { data: stripeFeeConfig } = useStripeFeeConfig();
  // Effective per-side transaction fee for a registration on this event:
  // a non-zero per-event `transaction_fee` override wins, otherwise it's
  // the platform-config-derived Stripe fee on entry + that row's
  // non-member surcharge. Mirrors the backend so cancel/refund/breakdown
  // estimates match what actually moves — auto-mode events store 0 in the
  // column but were grossed-up a real fee at checkout, and reading the
  // column raw would show "$0 transaction fee" and over-promise refunds.
  // `reg`, when supplied, prices THIS entry rather than the event in the
  // abstract: the backend already resolved its effective card fee, honouring
  // any per-entry admin waive. Recomputing from the event's shared fee would
  // quote a waived contestant a surcharge they don't owe.
  const perRegEffectiveTxnFee = (perRegNonMemberFee = 0, reg?: any): number => {
    const resolved = reg?.event_transaction_fee;
    if (resolved != null) return Math.max(0, Number(resolved) || 0);
    const evRow = event as any;
    const stored = Math.max(0, Number(evRow?.transaction_fee) || 0);
    if (stored > 0) return stored;
    if (!stripeFeeConfig) return 0;
    return calculateTransactionFee(
      (Number(evRow?.entry_fee) || 0) + Math.max(0, perRegNonMemberFee || 0),
      stripeFeeConfig,
    );
  };
  const cancelRegistration = useCancelTeamRegistration();
  const respondToPartner = useRespondToPartner();

  // Approve / reject the partner pairing on team_partner registrations
  // that are still `pending_confirmation*`. Approval bumps the row to
  // pending_payment so the standard Pay Now affordance below picks it
  // up; rejection cancels the partner row server-side and the primary
  // gets notified to pick someone else (handled in the cancel
  // workflow).
  const handlePartnerRespond = async (registrationId: string, accept: boolean) => {
    // Mirror the buddy-group invite gate: declines aren't blocked (a
    // user with an incomplete profile must still be able to back out
    // of an accidental invite), but accepting is — accepting locks
    // the user into the team-roping entry, and entering an event
    // already requires a full profile elsewhere. ProfileInfoTab honors
    // `returnTo` so the user pops back to this exact event after save.
    if (
      accept &&
      !isFullProfileComplete(profile as Record<string, unknown> | null)
    ) {
      showError(
        'Complete your profile first',
        'You need a full profile before you can accept partner invitations.',
      );
      navigate(
        `/profile?returnTo=${encodeURIComponent(`/events/${eventId}`)}`,
      );
      return;
    }
    setActionLoading(registrationId);
    try {
      await respondToPartner.mutateAsync({ registrationId, accept });
      showSuccess(
        accept ? 'Partner request accepted' : 'Partner request declined',
        accept
          ? 'Pay your entry to confirm your spot.'
          : 'The team primary has been notified.',
      );
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to respond');
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch parent rodeo's performances (perf + slack list w/ datetimes) so the
  // Event Info column can show the schedule. Generated CRUD endpoint accepts
  // `trig_rodeo_id` as a flat filter and is unauthenticated, so this works
  // for any viewer who can already see the event.
  const rodeoIdForPerf = (event as any)?.rodeo_id as string | undefined;
  const { data: rodeoPerformances } = useQuery<PerformancesRow[]>({
    queryKey: ['rodeo-performances', rodeoIdForPerf],
    queryFn: () =>
      new PerformancesClient().getAll({
        filters: { trig_rodeo_id: rodeoIdForPerf },
        sortBy: 'start_datetime',
        sortOrder: 'asc',
        limit: 200,
      }),
    enabled: !!rodeoIdForPerf,
  });

  // Fetch parent jackpot name for breadcrumbs
  const rodeoId = (event as any)?.rodeo_id;
  const { data: parentJackpot } = useJackpotDetail(rodeoId || '');
  // Buddy group context for the rodeo. Drives the buddy-group primary
  // exception below — primaries need to keep registering teammates on
  // behalf even after their OWN per-contestant cap is reached. Backend
  // returns null when the user isn't in a group for this rodeo, so this
  // is a no-op for non-buddy-group flows.
  //
  // We accept both `active` and `locked` statuses: `active` is the
  // pre-draw window where the primary can still add members; `locked`
  // is post-draw where new entries are blocked anyway by the entry-
  // close gate, so the cap message is moot. Only `disbanded` /
  // `pending` should hide the on-behalf CTA.
  const { data: myBuddyGroupForRodeo } = useMyBuddyGroup(
    rodeoId || '',
    isAuthenticated,
  );
  const isBuddyGroupPrimaryViaGroup =
    !!myBuddyGroupForRodeo &&
    (myBuddyGroupForRodeo.status === 'active' ||
      myBuddyGroupForRodeo.status === 'locked') &&
    !!myProfileId &&
    myBuddyGroupForRodeo.primary_member_user_profile_id === myProfileId;
  // Fallback path for the host-org eyebrow: not every event row has the
  // denormalised `trig_organization_name` populated (older rows pre-trigger,
  // or trigger gaps), so we resolve `primary_organization_id` from the
  // parent rodeo against the user's org memberships when the trig column
  // is missing.
  const { data: myOrganizations } = useOrganizations();
  const { setTrail } = useBreadcrumb();

  // Set full breadcrumb trail: [Rodeos|Jackpots] > [Parent Name] > [Event Name]
  // The parent route (Rodeos vs Jackpots) is chosen by the parent's
  // `rodeo_or_jackpot` discriminator so deep-links from /rodeos and
  // /jackpots both yield a coherent trail.
  useEffect(() => {
    const ev = event as any;
    if (!ev) return;

    const eventName = getEventDisplayTitle(ev);
    const parentName = parentJackpot?.name || ev.trig_rodeo_name;
    const isRodeo =
      (parentJackpot as any)?.rodeo_or_jackpot === 'rodeo' ||
      ev.trig_rodeo_or_jackpot === 'rodeo';
    const rootLabel = isRodeo ? 'Rodeos' : 'Jackpots';
    const rootPath = isRodeo ? '/rodeos' : '/jackpots';
    const parentPath = isRodeo ? `/rodeos/${rodeoId}` : `/jackpots/${rodeoId}`;

    const trail: { label: string; path: string }[] = [
      { label: rootLabel, path: rootPath },
    ];
    if (rodeoId && parentName) {
      trail.push({ label: parentName, path: parentPath });
    }
    if (eventName) {
      trail.push({ label: eventName, path: `/events/${eventId}` });
    }
    setTrail(trail);

    return () => setTrail(null);
  }, [eventId, event, rodeoId, parentJackpot, setTrail]);

  // Find ALL user registrations for this event (not just first)
  const myEventRegistrations = useMemo(() => {
    if (!myRegs || !Array.isArray(myRegs)) return [];
    return myRegs.filter((r: any) => r.event_id === eventId);
  }, [myRegs, eventId]);

  // Active (non-cancelled) registrations — includes both the viewer's
  // own rows and (when they are the buddy-group primary) teammate rows
  // tagged `is_primary_managed_view`.
  const activeRegistrations = useMemo(() => {
    return myEventRegistrations.filter(
      (r: any) => (r.trig_status_name || r.status) !== 'cancelled'
    );
  }, [myEventRegistrations]);

  // Fallback signal: `useMyRegistrations({ include_buddy_group: true })`
  // tags teammate rows with `is_primary_managed_view`. If the viewer is
  // seeing ANY of those for the rodeo, the backend has already confirmed
  // they're the primary cardholder — surface the on-behalf CTA even when
  // the dedicated buddy-group fetch hasn't resolved yet. Combined with
  // the explicit group-fetch check above (which works for primaries
  // whose teammates haven't registered yet), this covers both timings.
  const isBuddyGroupPrimaryViaManagedView = useMemo(() => {
    if (!Array.isArray(myRegs)) return false;
    return myRegs.some((r: any) => r?.is_primary_managed_view === true);
  }, [myRegs]);
  const isBuddyGroupPrimary =
    isBuddyGroupPrimaryViaGroup || isBuddyGroupPrimaryViaManagedView;

  // Position / cancellation tracking applies only to the viewer's own
  // rows. Teammate rows (managed by primary) shouldn't block the viewer
  // from registering themselves for a position.
  const ownActiveRegistrations = useMemo(
    () => activeRegistrations.filter((r: any) => !r.is_primary_managed_view),
    [activeRegistrations],
  );

  // Pull the user's upcoming-events list (already populated/cached by
  // the dashboard) so the EventDetailPage can detect mismatched
  // preferences across OTHER events at the SAME rodeo. Performance
  // preferences are stored per-registration but conceptually rodeo-
  // wide — entering Bareback Riding "Performance 1" + Barrel Racing
  // "Performance 2" at one rodeo is a real conflict the user wants
  // surfaced, even though those rows come from different events.
  // React Query dedupes the request, so this is free if the user
  // visited the dashboard first (the common path).
  const { data: upcomingEventsForUser } = useMyUpcomingEvents();
  const sameRodeoUpcomingEntries = useMemo(() => {
    if (!upcomingEventsForUser || !rodeoId) return [];
    return upcomingEventsForUser.filter(
      (r: any) => r?.rodeo?.id === rodeoId,
    );
  }, [upcomingEventsForUser, rodeoId]);

  // Conflict detection across every entry the viewer can SEE for this
  // rodeo: own rows on this event, buddy-group-primary-managed rows on
  // this event, AND own entries on OTHER events at this rodeo (from the
  // upcoming-events feed). De-dupe by id/registration_id since the
  // upcoming feed and the registration-ops feed both surface the
  // viewer's own row on this event. Backend ships partner_*_preference
  // enrichment, so single-row team registrations still surface a
  // conflict when the partner picked a different slot.
  const entryPreferenceConflict = useMemo(() => {
    const seen = new Set<string>();
    const combined: EntryWithPreferences[] = [];
    for (const r of activeRegistrations as any[]) {
      const id = r?.id as string | undefined;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      combined.push(r as EntryWithPreferences);
    }
    for (const r of sameRodeoUpcomingEntries as any[]) {
      const id = r?.registration_id as string | undefined;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      combined.push(r as EntryWithPreferences);
    }
    return detectConflictsForEvent(combined);
  }, [activeRegistrations, sameRodeoUpcomingEntries]);

  // Check if user can register for a specific position
  const registeredPositions = useMemo(() => {
    return new Set(ownActiveRegistrations.map((r: any) => r.position));
  }, [ownActiveRegistrations]);

  // Per-position counts of the viewer's own active rows. Drives the
  // team-roping multi-reg cap UX, where max=N means up to N Headers
  // AND N Heelers (so total cap is N*2). Solo events stick to the
  // bare count via `ownActiveRegistrations.length`.
  const ownPositionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of ownActiveRegistrations) {
      const pos = (r as any).position ?? '';
      counts[pos] = (counts[pos] ?? 0) + 1;
    }
    return counts;
  }, [ownActiveRegistrations]);

  // Buddy-group registration status — drives the "everyone in the
  // group has hit the cap" gate below. Without this, the on-behalf
  // CTA stays visible after the primary has registered themselves
  // AND every group member, leaving the user stuck on a button that
  // opens a modal where neither the self nor on-behalf path can
  // submit. Endpoint mirrors the one the registration modal uses so
  // backend-side backfill of solo registrations into the group is
  // reflected here too.
  const { data: buddyGroupStatus } = useBuddyGroupRegistrationStatus(
    eventId,
    myBuddyGroupForRodeo?.id,
    isBuddyGroupPrimary && !!myBuddyGroupForRodeo?.id,
  );
  const allBuddyGroupMembersAtCap = useMemo(() => {
    if (!isBuddyGroupPrimary) return false;
    const members = (myBuddyGroupForRodeo as any)?.members ?? [];
    // Mirror the modal's filter — pending invitations can't register,
    // so they shouldn't gate the at-cap check (otherwise an unanswered
    // invite would forever keep the CTA alive even after every active
    // member is registered).
    const registerable = members.filter(
      (m: any) => m.invitation_status !== 'pending',
    );
    if (registerable.length === 0) return false;
    const maxPerUser = Math.max(
      1,
      Number((event as any)?.max_registrations_per_user ?? 1) || 1,
    );
    const counts = buddyGroupStatus?.registration_counts ?? {};
    return registerable.every((m: any) => {
      const userId = m.user_profile_id;
      // For self use the local active count — the buddy-group-status
      // endpoint may not include the primary in `registration_counts`
      // depending on backend version, so falling back to the canonical
      // local count keeps the gate accurate.
      const count =
        userId && userId === myProfileId
          ? ownActiveRegistrations.length
          : (counts[userId] ?? 0);
      return count >= maxPerUser;
    });
  }, [
    isBuddyGroupPrimary,
    myBuddyGroupForRodeo,
    buddyGroupStatus,
    event,
    myProfileId,
    ownActiveRegistrations.length,
  ]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);

  /**
   * Cancellation deadline gate. Once the parent rodeo's entry_close_time
   * has passed, the team has been built and partners have been paired —
   * yanking a row out of that arrangement after the fact creates messy
   * refund and team-integrity edge cases. The Cancel buttons are hidden
   * past this point; the backend cancel-team route enforces the same
   * cutoff with a 409 ENTRY_CLOSE_PASSED so the rule can't be bypassed
   * via direct API call.
   */
  const entriesHaveClosed = useMemo(() => {
    const closeAt =
      (parentJackpot as any)?.entry_close_time ??
      (event as any)?.trig_rodeo_entry_close_time ??
      null;
    if (!closeAt) return false;
    const closeMs = new Date(closeAt).getTime();
    return Number.isFinite(closeMs) && closeMs <= Date.now();
  }, [parentJackpot, event]);

  // Best-effort refund estimate for the cancel dialog. Pulled from the
  // registration row + event so the user sees what's coming back before
  // confirming. Backend is source of truth — actual refund amounts come
  // back in the cancel response and surface in the success toast. The
  // shape mirrors the backend's CancellationRefundBreakdown so the dialog
  // can show itemized entry / non-member / transaction lines.
  const cancelRefundEstimate = useMemo(() => {
    if (!pendingCancelId) return undefined;
    const reg = ownActiveRegistrations.find(
      (r: any) => r.id === pendingCancelId,
    ) as any;
    if (!reg) return undefined;
    const paid = Number(reg?.payment_amount);
    // /my-registrations enriches with `payment_provider` (not
    // `payment_method` — the registration row has no such column).
    // Accept both 'stripe' (current) and 'paypal' (pre-migration data
    // still in the DB) so post-Stripe-migration users actually see the
    // refund breakdown when cancelling — checking only 'paypal' silently
    // dropped every Stripe-paid registration into the no-breakdown
    // fallback copy.
    const provider = reg?.payment_provider;
    const isPaidOnline =
      (provider === 'stripe' || provider === 'paypal') &&
      (reg?.payment_status === 'completed' ||
        reg?.payment_status === 'paid' ||
        reg?.trig_status_name === 'confirmed');
    if (!isPaidOnline || !Number.isFinite(paid) || paid <= 0) return undefined;
    const ev = event as any;
    const perRegEntryFee = Math.max(0, Number(ev?.entry_fee) || 0);
    const perRegNonMemberFee = Math.max(0, Number(reg?.non_member_fee) || 0);
    // Doubling applies ONLY when this cancel will cascade to BOTH halves
    // of the team — i.e. the user is the team_primary on a still-paired
    // paid_for_partner registration. Once the partner has cancelled
    // (registration_type flips to 'individual'), this cancel only affects
    // the user's own seat, so the breakdown + refund estimate must use
    // the per-side numbers — otherwise we'd promise $260 back when only
    // $130 is left to refund.
    const isFullTeamCancel =
      !!reg?.paid_for_partner && reg?.registration_type === 'team_primary';
    const multiplier = isFullTeamCancel ? 2 : 1;
    const entryFee = perRegEntryFee * multiplier;
    const nonMemberFee = perRegNonMemberFee * multiplier;
    // Match the captured amount to what the cascade will actually refund.
    // Bulk: payment_amount is the per-side share, so double it to reflect
    // the full bulk capture when the cancel cascades to both rows. Legacy
    // paid_for_partner already stored the doubled total on the primary,
    // so there's nothing to double here. For broken-team rows the per-
    // side stored value is exactly right.
    const isBulkPayment = (reg as any)?.payment_is_bulk === true;
    // Subtract any supplemental balance the user STILL owes from the
    // captured-amount basis. After a membership rejection, payment_amount
    // is bumped by the surcharge but the user may not have paid that
    // supplemental yet — refunding `payment_amount - transaction_fee`
    // would over-promise (we can't refund money that never arrived). The
    // outstanding-balance bucket reports the unpaid diff; subtracting it
    // gives the actual captured total.
    const supplementalUnpaid = owedByRegistrationId.get(reg.id) ?? 0;
    const grossPaid =
      isFullTeamCancel && isBulkPayment ? paid * 2 : paid;
    const totalPaid = Math.max(0, grossPaid - supplementalUnpaid);
    // Prorate the breakdown: a still-owed supplemental (almost always the
    // post-rejection non-member surcharge) wasn't in the capture, so it
    // isn't being refunded — drop it from the surcharge line shown.
    const nonMemberFeeShown = Math.max(0, nonMemberFee - supplementalUnpaid);
    // Transaction fee that was ACTUALLY in the captured amount. The
    // recomputed fee (grossed up on entry + surcharge) can be larger than
    // what was really charged when the surcharge landed in a separate
    // capture — never retain/show more than `totalPaid − entry − captured
    // surcharge`. Mirrors RegistrationPaymentService.processCancellationRefund.
    const recomputedTxnFee = perRegEffectiveTxnFee(perRegNonMemberFee) * multiplier;
    const transactionFee = Math.max(
      0,
      Math.min(
        recomputedTxnFee,
        Math.round((totalPaid - entryFee - nonMemberFeeShown) * 100) / 100,
      ),
    );
    const refundable = Math.max(0, totalPaid - transactionFee);
    return {
      paidTotal: totalPaid,
      entryFee,
      nonMemberFee: nonMemberFeeShown,
      transactionFee,
      refundable,
      currency: 'USD' as const,
    };
  }, [pendingCancelId, ownActiveRegistrations, event, owedByRegistrationId, stripeFeeConfig]);

  // Header totals for the bulk-pay basket so the dialog shows the
  // aggregated amount before the order mints (real total comes back
  // from the backend). Sums each unpaid row using the
  // outstanding-balance override when present (handles supplemental
  // diffs accurately) and falling back to `payment_amount` for
  // pending_payment rows. Keeps the math the same shape PaymentsTab
  // already uses for its bulk-pay header.
  const bulkPayTotal = useMemo(() => {
    if (!bulkPayIds || bulkPayIds.length === 0) return null;
    const idSet = new Set(bulkPayIds);
    return ownActiveRegistrations
      .filter((r: any) => idSet.has(r.id))
      .reduce((sum: number, r: any) => {
        const override = owedByRegistrationId.get(r.id);
        if (override != null) return sum + override;
        return sum + (Number(r.payment_amount) || 0);
      }, 0);
  }, [bulkPayIds, ownActiveRegistrations, owedByRegistrationId]);

  // Sum of transaction fees baked into the bulk total. Surfaced in the
  // dialog so the user knows part of "Total due" is non-refundable on
  // cancel. Doubles per-row when paid_for_partner is true since the
  // primary covered both seats' transaction fee.
  const bulkPayTransactionFee = useMemo(() => {
    if (!bulkPayIds || bulkPayIds.length === 0) return null;
    const idSet = new Set(bulkPayIds);
    return ownActiveRegistrations
      .filter((r: any) => idSet.has(r.id))
      .reduce((sum: number, r: any) => {
        const status = r.trig_status_name || r.status;
        // Supplemental rows (confirmed-with-balance) had their txn fee
        // captured at original payment time — the supplemental delta
        // is non-member surcharge only, no extra txn fee owed.
        if (status !== 'pending_payment') return sum;
        const txn = perRegEffectiveTxnFee(Math.max(0, Number(r.non_member_fee) || 0));
        return sum + (r.paid_for_partner ? txn * 2 : txn);
      }, 0);
  }, [bulkPayIds, ownActiveRegistrations, event, stripeFeeConfig]);

  // rodeoId already declared above for breadcrumbs + back navigation

  // Deep-link entry: when the user lands here via ?action=pay (e.g. from
  // the dashboard "Pay Now" button or the event card), auto-open the
  // payment dialog against EVERY pending_payment registration the user
  // owns on this event — not just the first one we trip over. The
  // EventCard CTA represents "settle my outstanding balance for this
  // event in one shot", which on team-roping events routinely means a
  // buddy-group team_primary row plus a solo entry needing one bulk
  // charge. Picking the first row alone left users staring at half the
  // total they thought they were paying.
  //
  // Routing rule mirrors the Funds tab:
  //   • 1 unpaid row  → single PayNowDialog (one Stripe intent)
  //   • 2+ unpaid rows → BulkPayDialog (one Stripe intent for the lot)
  //
  // We strip the param either way so refresh / back doesn't re-trigger
  // the modal after the user closes it.
  //
  // IMPORTANT: gate on `loadingMyRegs` finishing first. On a fresh
  // navigation `myRegs` is still undefined for the first render, so
  // `ownActiveRegistrations` is `[]`, no target is found, and we'd
  // strip ?action=pay before the registrations ever loaded — leaving
  // the user staring at the page with no modal until they bounced and
  // tried again. Wait for the query to resolve, *then* decide.
  useEffect(() => {
    if (searchParams.get('action') !== 'pay') return;
    if (loadingMyRegs || !myRegs) return;
    const owedTargets = ownActiveRegistrations.filter(
      (r: any) => (r.trig_status_name || r.status) === 'pending_payment',
    );
    if (owedTargets.length === 1) {
      setPayNowRegistrationId(owedTargets[0].id);
    } else if (owedTargets.length > 1) {
      setBulkPayIds(owedTargets.map((r: any) => r.id));
    }
    // Clear the query param either way — if there's no pending registration
    // to settle there's nothing for the dialog to do.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, loadingMyRegs, myRegs, ownActiveRegistrations, setSearchParams]);

  const handleCancelClick = (registrationId: string) => {
    setPendingCancelId(registrationId);
  };

  // Pay Now entry point. When the row is mid-ACH-verification we warn first so
  // the user explicitly confirms they want to abandon that pending bank
  // verification; a plain unpaid row opens checkout directly.
  const handlePayNowClick = (reg: any, eventName: string | null) => {
    if (reg?.ach_verification_pending) {
      setVerifyWarn({ id: reg.id, eventName });
      return;
    }
    setPayNowRegistrationId(reg.id);
  };

  const handleCancelConfirm = async () => {
    if (!pendingCancelId) return;
    const registrationId = pendingCancelId;
    setActionLoading(registrationId);
    try {
      const result = await cancelRegistration.mutateAsync(registrationId);
      // If the backend issued a refund, mention the amount so the user
      // knows it's coming and that the transaction fee was retained.
      const successfulRefund = result?.refunds?.find((r) => r.refunded);
      if (successfulRefund?.amount_refunded != null) {
        const refunded = formatCurrency(successfulRefund.amount_refunded);
        const fee = successfulRefund.transaction_fee_retained ?? 0;
        const detail =
          fee > 0
            ? `Refunded ${refunded} to your original payment method. The ${formatCurrency(fee)} transaction fee is non-refundable.`
            : `Refunded ${refunded} to your original payment method.`;
        showSuccess('Entry cancelled', detail);
      } else {
        showSuccess('Entry cancelled');
      }
      setPendingCancelId(null);
    } catch {
      showError('Failed to cancel entry');
    } finally {
      setActionLoading(null);
    }
  };

  if (loadingEvent) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <EmptyState
          icon={Calendar}
          title="Event not found"
          description="This event may have been removed or is not published."
        />
      </div>
    );
  }

  const ev = event as any;
  const capacityPct =
    capacity && capacity.total_capacity > 0
      ? Math.round((capacity.registered_count / capacity.total_capacity) * 100)
      : null;

  // Handicaps are jackpot-only — drop handicap and event-format UI when the
  // parent is a rodeo. Same discriminator the breadcrumb effect above uses.
  const isRodeo =
    (parentJackpot as any)?.rodeo_or_jackpot === 'rodeo' ||
    ev.trig_rodeo_or_jackpot === 'rodeo';

  // Finals rodeos seat every contestant in every performance — no buddy
  // groups, no performance preferences. Prefer the policy value (auth-only)
  // and fall back to the rodeo record for anonymous viewers.
  const isFinalsRodeo =
    (policyData as any)?.is_finals === true ||
    (parentJackpot as any)?.is_finals === true;

  // Non-member surcharge applies when the org has member pricing enabled and
  // the viewer isn't a member of the host org. /registration-policy requires
  // auth, so anonymous viewers won't have policyData and we fall through to
  // member-priced display until they log in.
  const nonMemberSurcharge =
    policyData?.member_fee_enabled &&
    policyData.is_member === false &&
    (policyData.non_member_surcharge ?? 0) > 0
      ? Number(policyData.non_member_surcharge)
      : 0;

  // Resolve the effective transaction fee using the same fallback chain as
  // the backend (PaymentService.getEffectiveTransactionFee) and the wizard:
  // honor a non-zero per-event override, otherwise derive from the platform
  // Stripe fee config so auto-mode events surface the fee Stripe will
  // actually charge instead of dropping the line entirely. The hook itself
  // is called near the top of the component to keep the hook order stable
  // across the loading/error early-returns.
  //
  // Include the non-member surcharge in the basis: Stripe takes its cut
  // from the FULL captured amount, so if the user is paying entry +
  // surcharge the transaction fee must cover the surcharge piece too —
  // otherwise the platform eats the difference at capture time.
  const storedTxnFee = Math.max(0, Number(ev.transaction_fee) || 0);
  const autoTxnFee = stripeFeeConfig
    ? calculateTransactionFee(
        (Number(ev.entry_fee) || 0) + nonMemberSurcharge,
        stripeFeeConfig,
      )
    : 0;
  const effectiveTransactionFee = storedTxnFee > 0 ? storedTxnFee : autoTxnFee;

  // Total fee calculation — includes the non-member surcharge when it
  // applies to the current viewer so "Total to Enter" is accurate.
  const totalFee =
    (ev.entry_fee ?? 0) + effectiveTransactionFee + nonMemberSurcharge;

  // Format draw/pick info
  const hasDrawInfo = (ev.pick_count ?? 0) > 0 || (ev.draw_count ?? 0) > 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => (rodeoId ? navigate(`/jackpots/${rodeoId}`) : navigate(-1))}
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      {/* ─── Hero Header ──────────────────────────────── */}
      <div className="space-y-2">
        {/* Parent org/rodeo eyebrow — surfaces both the host organization and
            the parent rodeo (or jackpot) above the title so the viewer
            immediately knows who is running it and which rodeo this event
            belongs to. Org name comes from `trig_organization_name` (denorm
            trigger column on events). Rodeo name resolves the same way the
            breadcrumb does (parentJackpot.name with a trig_rodeo_name
            fallback) since not every event row has the denormalised trig
            column populated. Only the rodeo half is clickable — there's no
            org detail page to route to today. */}
        {(() => {
          const parentName =
            (parentJackpot as any)?.name || ev.trig_rodeo_name || null;
          // Try the trigger column first; fall back to resolving the
          // parent rodeo's primary_organization_id against the user's org
          // memberships. Without this fallback, events whose trigger
          // hasn't fired (or rows older than the trigger) render with a
          // bare rodeo name and no host-org context.
          const orgIdFromParent =
            typeof (parentJackpot as any)?.primary_organization_id === 'string'
              ? (parentJackpot as any).primary_organization_id
              : null;
          const orgFromMembership = orgIdFromParent
            ? ((myOrganizations ?? []).find(
                (o: any) => o?.id === orgIdFromParent,
              ) as { name?: string } | undefined)
            : undefined;
          const orgName =
            ev.trig_organization_name ||
            orgFromMembership?.name ||
            null;
          if (!parentName && !orgName) return null;
          return (
            <p className="text-sm flex flex-wrap items-center gap-1.5">
              {orgName && (
                <span className="font-medium text-muted-foreground">
                  {orgName}
                </span>
              )}
              {orgName && parentName && (
                <span className="text-muted-foreground/60">›</span>
              )}
              {parentName && (
                <button
                  type="button"
                  className="font-medium text-primary hover:underline underline-offset-2"
                  onClick={() =>
                    rodeoId &&
                    navigate(isRodeo ? `/rodeos/${rodeoId}` : `/jackpots/${rodeoId}`)
                  }
                >
                  {parentName}
                </button>
              )}
            </p>
          );
        })()}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">{getEventDisplayTitle(ev)}</h1>
          {ev.trig_status_name && <StatusBadge status={ev.trig_status_name} variant="event" />}
          {ev.is_century_jackpot && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">Century</Badge>
          )}
          {ev.is_mixed_gender && (
            <Badge variant="outline">Mixed Gender</Badge>
          )}
          {ev.allow_multiple_registrations && (ev.max_registrations_per_user ?? 1) > 1 && (
            <Badge
              variant="outline"
              className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
            >
              {isTeamRopingEvent(ev) && (ev as any).enforce_balanced_positions
                ? `Up to ${ev.max_registrations_per_user} entries per contestant — ${describeBalancedSplit(Number(ev.max_registrations_per_user) || 1)}`
                : `Up to ${ev.max_registrations_per_user} entries per contestant`}
            </Badge>
          )}
        </div>
        {/* Date context — the org and rodeo names moved into the eyebrow
            above so this row stays focused on the rodeo's date range. */}
        {(ev.trig_rodeo_start_date || ev.trig_rodeo_end_date) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDateRange(ev.trig_rodeo_start_date, ev.trig_rodeo_end_date)}
            </span>
          </div>
        )}
      </div>

      {/* ─── Quick Stats ──────────────────────────────── */}
      {(() => {
        const stats: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }[] = [
          { icon: DollarSign, label: 'Entry Fee', value: formatCurrency(ev.entry_fee) },
        ];
        // Combined-handicap cap is a jackpot-only concept. Rodeos don't run
        // off contestant handicaps, so the card is misleading there.
        if (!isRodeo) {
          stats.push({
            icon: Target,
            label: 'Combined Max HC',
            value: ev.max_combined_handicap != null ? String(ev.max_combined_handicap) : '-',
          });
        }
        // Prize is gated entirely by the "display to contestants" toggle and
        // rendered by the dedicated EventPrizeBreakdownCard below (live total +
        // breakdown). No static prize quick-stat — off means hidden everywhere.
        // Backfill remaining slots with max contestants — transaction fee
        // is intentionally excluded from the hero stats: it lives in Entry
        // Details where it belongs and shouldn't dominate the top of the
        // page.
        // Treat 0 same as null (= unlimited). The Fastify body validator
        // coerces null on numeric fields to 0, so a "blank to clear" save
        // round-trips back as 0; without this guard the stat reads as
        // "Max Contestants: 0" which is misleading.
        if (
          stats.length < 4 &&
          typeof ev.max_contestants === 'number' &&
          ev.max_contestants > 0
        ) {
          stats.push({ icon: Users, label: 'Max Contestants', value: String(ev.max_contestants) });
        }
        return (
          <div className={`grid grid-cols-2 ${stats.length >= 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-3`}>
            {stats.map((s) => (
              <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} />
            ))}
          </div>
        );
      })()}

      {/* ─── Event Details ──────────────────────────────── */}
      <Card>
        <CardContent className="p-5">
          {/* Rodeos: 3-col layout — Entry Details (fees) | Event Info (draw,
              rules, ops) | Performances & Slacks. Jackpots keep the original
              3-col Handicap | Format | Entry layout. */}
          <div
            className={
              isRodeo
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3'
                : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3'
            }
          >
            {/* Handicap section — jackpot-only; not meaningful for rodeos. */}
            {!isRodeo && (
              <div className="space-y-2">
                <SectionLabel
                  icon={<Target className="h-3.5 w-3.5" />}
                  color="muted"
                >
                  Handicap Limits
                </SectionLabel>
                <DetailRow label="Header Max" value={ev.header_max_handicap} />
                <DetailRow label="Heeler Max" value={ev.heeler_max_handicap} />
                <DetailRow label="Combined Max" value={ev.max_combined_handicap} />
                <DetailRow label="Individual Max" value={ev.max_individual_handicap} />
                {ev.handicap_type && (
                  <DetailRow label="Type" value={ev.handicap_type} capitalize />
                )}
              </div>
            )}

            {/* Format & Rules section — the format/elimination/picks fields
                describe jackpot draw mechanics that don't apply at the rodeo
                event level. */}
            {!isRodeo && (
              <div className="space-y-2">
                <SectionLabel
                  icon={<Zap className="h-3.5 w-3.5" />}
                  color="muted"
                >
                  Format &amp; Rules
                </SectionLabel>
                {ev.event_format && (
                  <DetailRow label="Format" value={ev.event_format.replace(/_/g, ' ')} capitalize />
                )}
                <DetailRow label="Rounds" value={ev.total_rounds} />
                {ev.elimination_enabled && (
                  <>
                    <DetailRow label="Short Round Size" value={ev.short_round_size} />
                    <DetailRow label="Short Round Count" value={ev.short_round_count} />
                  </>
                )}
                {hasDrawInfo && (
                  <>
                    <DetailRow label="Picks" value={ev.pick_count} />
                    <DetailRow label="Draws" value={ev.draw_count} />
                    {ev.enforce_mutual_picks && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        Mutual picks required
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Fees & Entry section. For rodeos this column is fee-focused
                (operational toggles like buddy groups / solo entry move to
                the Event Info column on the right). */}
            <div className="space-y-2">
              <SectionLabel
                icon={<DollarSign className="h-3.5 w-3.5" />}
                color="muted"
              >
                Entry Details
              </SectionLabel>
              <DetailRow label="Entry Fee" value={formatCurrency(ev.entry_fee)} />
              {effectiveTransactionFee > 0 && (
                <DetailRow
                  label="Transaction Fee"
                  value={formatCurrency(effectiveTransactionFee)}
                  hint="Waived when paying via bank transfer (ACH)"
                />
              )}
              {nonMemberSurcharge > 0 && (
                <DetailRow
                  label="Non-Member Fee"
                  value={formatCurrency(nonMemberSurcharge)}
                />
              )}
              {totalFee > 0 && totalFee !== (ev.entry_fee ?? 0) && (
                <DetailRow label="Total to Enter" value={formatCurrency(totalFee)} bold />
              )}
              {ev.slide_type && ev.slide_type !== 'NONE' && (
                <>
                  <DetailRow label="Slide" value={ev.slide_type} capitalize />
                  {ev.slide_conversion_rate != null && (
                    <DetailRow label="Slide Rate" value={`${ev.slide_conversion_rate}x`} />
                  )}
                </>
              )}
              {/* Operational flags stay in Entry Details for jackpots; the
                  rodeo layout pulls them into the Event Info column below. */}
              {!isRodeo && ev.allow_no_partner_entry && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                  Solo entry allowed
                </div>
              )}
              {!isRodeo && ev.allow_multiple_registrations && (
                <DetailRow label="Max Entries" value={String(ev.max_registrations_per_user ?? 'Unlimited')} />
              )}
              {!isRodeo && ev.buddy_groups_enabled && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Users className="h-3 w-3 text-blue-500" />
                  Buddy groups (max {ev.max_buddy_group_size})
                </div>
              )}
            </div>

            {/* Event Info — rodeo-only right column with entry-close
                timing, rulesets, payment options, contestant cap, the
                rodeo's performance/slack schedule, and the operational
                flags pulled from Entry Details. */}
            {isRodeo && (() => {
              const closeAt = (policyData as any)?.entry_close_time ?? (parentJackpot as any)?.entry_close_time;
              const allowCash = (policyData as any)?.allow_cash_payment;
              // Pay Later is a rodeo switch with a per-event override, already
              // resolved by the policy endpoint. Absent (older payload) →
              // treat as on, which is the historical behavior.
              const allowPayLater = (policyData as any)?.allow_pay_later ?? true;
              // Whether the deferred options are members-only depends on the
              // resolved membership requirement — an open rodeo offers them to
              // everyone, so the "(members only)" qualifier would be wrong.
              const deferredMembersOnly =
                (policyData as any)?.member_fee_enabled === true;
              const feesDueMessage = (policyData as any)?.fees_due_message;
              // Pin display TZ to the rodeo's saved value so the wall-clock
              // matches what the host entered. Policy fetch is auth-gated;
              // anonymous viewers fall back to the rodeo row, then Pacific
              // (the column's DB default) so legacy rows still render.
              const eventRodeoTz =
                (policyData as any)?.timezone ||
                (parentJackpot as any)?.timezone ||
                'America/Los_Angeles';
              const rulesets: Array<{ id: string; name: string }> =
                (eventRulesetsData as any)?.rulesets ?? [];
              return (
                <div className="space-y-2">
                  <SectionLabel
                    icon={<Info className="h-3.5 w-3.5" />}
                    color="muted"
                  >
                    Event Info
                  </SectionLabel>
                  {closeAt && (
                    <DetailRow
                      label="Entries Close"
                      value={formatInTimezone(closeAt, eventRodeoTz, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    />
                  )}
                  {/* Current entry/team count — mirrors the EventCard
                      capacity row's team-roping treatment: the registrations
                      table stores 1 row per person (Header + Heeler), so we
                      divide the count by 2 for team-roping events. Ceil so an
                      orphan partner row counts as a team in progress. Cap
                      (max_contestants) is the people-cap; floor to teams. */}
                  {(() => {
                    const isTeamEvent = isTeamRopingEvent(ev);
                    const registered = capacity?.registered_count ?? 0;
                    const total =
                      typeof ev.max_contestants === 'number' && ev.max_contestants > 0
                        ? ev.max_contestants
                        : 0;
                    const displayCount = isTeamEvent ? Math.ceil(registered / 2) : registered;
                    const displayTotal = isTeamEvent ? Math.floor(total / 2) : total;
                    const value =
                      total > 0
                        ? `${displayCount} of ${displayTotal}`
                        : String(displayCount);
                    return (
                      <DetailRow
                        label={isTeamEvent ? 'Teams So Far' : 'Entries So Far'}
                        value={value}
                      />
                    );
                  })()}
                  {typeof ev.max_contestants === 'number' && ev.max_contestants > 0 && (
                    <DetailRow
                      label="Contestant Limit"
                      value={String(ev.max_contestants)}
                    />
                  )}
                  {/* Payment Options: comma-joined list of supported methods.
                      Card and ACH (via Stripe) are always available. Pay Later
                      and Cash at Event are opt-in per rodeo, and are
                      members-only perks whenever the rodeo requires membership
                      (server-enforced — see registration.service.ts). On a
                      rodeo that doesn't require membership they're open to
                      everyone, so the qualifier is dropped.
                      The rulesets list rides as small italic text below so
                      the two read together without burning a full labeled
                      section on rules. */}
                  {(() => {
                    const methods: string[] = ['Card', 'ACH'];
                    const qualifier = deferredMembersOnly ? ' (members only)' : '';
                    if (allowPayLater) methods.push(`Pay Later${qualifier}`);
                    if (allowCash) methods.push(`Cash${qualifier}`);
                    return (
                      <div className="space-y-0.5">
                        <DetailRow
                          label="Payment Options"
                          value={methods.join(', ')}
                        />
                        {rulesets.length > 0 && (
                          <p className="text-xs text-muted-foreground italic text-right">
                            {rulesets.map((r) => r.name).join(', ')}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  {ev.allow_multiple_registrations && (
                    <DetailRow
                      label="Max Entries"
                      value={String(ev.max_registrations_per_user ?? 'Unlimited')}
                    />
                  )}
                  {ev.allow_no_partner_entry && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      Solo entry allowed
                    </div>
                  )}
                  {ev.buddy_groups_enabled && !isFinalsRodeo && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Users className="h-3 w-3 text-blue-500" />
                      Buddy groups (max {ev.max_buddy_group_size})
                    </div>
                  )}
                  {feesDueMessage && (
                    <p className="pt-1 text-xs text-muted-foreground italic">
                      {feesDueMessage}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Performances & Slacks — rodeo-only third column. Sorted by
                start_datetime so the schedule reads top-to-bottom. */}
            {isRodeo && (rodeoPerformances?.length ?? 0) > 0 && (() => {
              const perfTz =
                (policyData as any)?.timezone ||
                (parentJackpot as any)?.timezone ||
                'America/Los_Angeles';
              return (
              <div className="space-y-2">
                <SectionLabel
                  icon={<Calendar className="h-3.5 w-3.5" />}
                  color="muted"
                >
                  Performances &amp; Slacks
                </SectionLabel>
                <ul className="text-sm space-y-1">
                  {[...(rodeoPerformances ?? [])]
                    .sort((a, b) => {
                      const ta = a.start_datetime ? new Date(a.start_datetime).getTime() : Infinity;
                      const tb = b.start_datetime ? new Date(b.start_datetime).getTime() : Infinity;
                      return ta - tb;
                    })
                    .map((p) => {
                      const dt = p.start_datetime
                        ? formatInTimezone(p.start_datetime, perfTz, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : null;
                      return (
                        <li key={p.id} className="flex items-start justify-between gap-3">
                          <span className="font-medium flex items-center gap-1.5">
                            {p.performance_name || (p.is_slack ? 'Slack' : 'Performance')}
                            {p.is_slack && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
                                Slack
                              </Badge>
                            )}
                          </span>
                          {dt && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {dt}
                            </span>
                          )}
                        </li>
                      );
                    })}
                </ul>
                {/* Per-event default cap that the draw applies to each
                    performance. Slack is uncapped and absorbs any overflow.
                    NULL or 0 = no explicit default; the draw falls back to
                    splitting `max_contestants` across the available perfs.
                    (0 is treated like NULL because Fastify's body validator
                    coerces null→0.) */}
                {(() => {
                  const cpp = (ev as any).contestants_per_performance;
                  return typeof cpp === 'number' && cpp > 0 ? (
                    <p className="pt-1 text-xs text-muted-foreground">
                      {cpp} contestant{cpp === 1 ? '' : 's'} per performance
                    </p>
                  ) : null;
                })()}
              </div>
              );
            })()}
          </div>

          {/* Capacity bar — full width below the columns */}
          {capacityPct !== null && (() => {
            const registered = capacity!.registered_count;
            const total = capacity!.total_capacity;
            const isOver = registered > total;
            const over = isOver ? registered - total : 0;
            const overPct = isOver ? Math.round((over / total) * 100) : 0;
            const spotsLeft = capacity!.available_spots;

            return (
              <div className="mt-5 pt-4 border-t space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span className={isOver ? 'text-destructive font-medium' : ''}>
                      {registered} / {total} registered
                    </span>
                  </span>
                  <span
                    className={
                      isOver
                        ? 'text-destructive font-medium'
                        : spotsLeft <= 5
                          ? 'text-amber-600 font-medium'
                          : ''
                    }
                  >
                    {isOver
                      ? `Full · ${over} over (+${overPct}%)`
                      : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                  </span>
                </div>
                <CapacityBar registered={registered} total={total} />
                {capacity!.waitlist_count > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {capacity!.waitlist_count} on waitlist
                  </p>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* ─── Prize Pool (live total + per-round/per-place breakdown). Sits
          under Entry Details so the prize structure follows the fee details. ─── */}
      <EventPrizeBreakdownCard
        eventId={ev.id}
        displayEnabled={!!(ev as any).display_prize_to_contestants}
        performances={rodeoPerformances}
      />

      {/* ─── Public Draw Results (renders only when admin has toggled
              `draw_results_visible=true` AND there's a published/finalized
              execution; otherwise the component returns null and this slot
              collapses to nothing). ─────────────────────────────────────── */}
      {ev?.id && <PublicDrawResultsCard eventId={ev.id} />}

      {/* ─── Live Results (renders only once the event has been performed —
              at least one scored run; polls so it tracks the secretary's
              score entry. Adapts time vs. score by event type).
              Rodeo events gate on the admin's per-event `show_results` toggle;
              jackpot events keep the legacy auto-show-once-scored behavior. ── */}
      {ev?.id && (!((ev as any).rodeo_id) || (ev as any).show_results) && (
        <PublicEventResultsCard eventId={ev.id} event={ev as any} />
      )}

      {/* ─── Your Registrations ──────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Your Entries
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Soft warning banner shown when the viewer's visible entries
              for this event have diverging performance preferences.
              Common cases: a team-roping partner who picked a different
              slot than the buddy-group entry, or two multi-reg entries
              on the same event that don't agree. Informational, not
              blocking — copy says "different" so users with intentional
              divergence (e.g. team partner outside their buddy group)
              don't read it as an error. */}
          {entryPreferenceConflict.hasConflict && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <p className="font-medium">
                  {entryPreferenceConflict.kind === 'team_partner'
                    ? 'Different preferences from partner'
                    : entryPreferenceConflict.kind === 'multi_entry'
                      ? 'Different preferences across your entries at this rodeo'
                      : 'Different preferences detected'}
                </p>
                <p className="text-xs text-amber-800/90 dark:text-amber-200/90">
                  {entryPreferenceConflict.reason} Performance preferences
                  apply across the whole rodeo, so mismatches here mean
                  one of your entries may end up in a different slot than
                  the others. This is informational only — if it's what
                  you intended, you can ignore it.
                </p>
              </div>
            </div>
          )}

          {/* Show all active registrations, grouped into teams. Two
              registrations that point at each other via
              partner_registration_id are rendered together inside a
              labelled "Team" container so the Header / Heeler pairing
              is unmistakable. Solo entries (or rows whose partner row
              isn't visible to the viewer) just render alone. */}
          {(() => {
            const regById = new Map<string, any>();
            for (const r of activeRegistrations as any[]) regById.set(r.id, r);
            const teams: any[][] = [];
            const seenIds = new Set<string>();
            for (const r of activeRegistrations as any[]) {
              if (seenIds.has(r.id)) continue;
              const partnerId = r.partner_registration_id;
              const partner = partnerId ? regById.get(partnerId) : null;
              if (partner && !seenIds.has(partner.id)) {
                // Order primary first so the YOUR ENTRY card sits
                // above the PARTNER ENTRY card visually.
                const primary =
                  r.registration_type === 'team_partner' ? partner : r;
                const partnerRow = primary === r ? partner : r;
                teams.push([primary, partnerRow]);
                seenIds.add(r.id);
                seenIds.add(partner.id);
              } else {
                teams.push([r]);
                seenIds.add(r.id);
              }
            }
            // Derived once for the side-entry indicator on every card.
            const userInBuddyGroupAtRodeo =
              !!myBuddyGroupForRodeo &&
              (myBuddyGroupForRodeo.status === 'active' ||
                myBuddyGroupForRodeo.status === 'locked');
            const userBuddyGroupName: string | null =
              userInBuddyGroupAtRodeo
                ? myBuddyGroupForRodeo?.group_name ?? null
                : null;
            return teams.length > 0 ? (
            <div className="space-y-4">
              {teams.map((team, teamIdx) => {
                // Pick a name for the "Team — with X" label. From the
                // primary's perspective the partner is the other side;
                // from the partner's perspective (managed view) the
                // partner-name is on the row already.
                const isPair = team.length > 1;
                const nameOf = (r: any): string | null =>
                  (r.trig_display_name ??
                    r.trig_user_full_name ??
                    [r.trig_first_name, r.trig_last_name]
                      .filter(Boolean)
                      .join(' ')) ||
                  null;
                const teamLabelName = isPair
                  ? // The partner-side row (managed view) carries the
                    // partner's name in the trig_* columns; fall back
                    // to the primary's partner_name field.
                    nameOf(team.find((r) => r.is_primary_managed_view) ??
                      team[1]) ||
                    team[0]?.partner_name ||
                    null
                  : null;
                return (
                  <div key={team[0].id} className="space-y-2">
                    {isPair && (
                      <SectionLabel
                        icon={<Users className="h-3.5 w-3.5" />}
                        color="muted"
                      >
                        {teamLabelName
                          ? `Team — with ${teamLabelName}`
                          : `Team #${teamIdx + 1}`}
                      </SectionLabel>
                    )}
                    {team.map((reg: any) => {
                const regStatus = reg.trig_status_name || reg.status || 'pending';
                const groupName: string | null = reg.buddy_group_name ?? null;
                const inBuddyGroup = !!reg.buddy_group_id;
                const isManagedView = !!reg.is_primary_managed_view;
                const memberName: string | null =
                  (reg.trig_display_name ??
                    reg.trig_user_full_name ??
                    [reg.trig_first_name, reg.trig_last_name]
                      .filter(Boolean)
                      .join(' ')) ||
                  null;
                // Entry-type variant drives the card's colour family +
                // header strip so a Header-and-Heeler view (your row plus
                // your partner's row) reads as two distinct entries
                // instead of two near-identical cards. Buddy-group
                // members get the same violet treatment as the
                // dashboard's buddy-group rows for consistency.
                type EntryType = 'mine' | 'partner' | 'buddy_member';
                const entryType: EntryType = isManagedView
                  ? reg.registration_type === 'team_partner'
                    ? 'partner'
                    : 'buddy_member'
                  : 'mine';
                const entryStyle = {
                  mine: {
                    border:
                      'border-primary/40 dark:border-primary/50',
                    stripe: 'bg-primary/70',
                    header:
                      'bg-primary/10 text-primary border-b border-primary/20',
                    label: 'Your Entry',
                    icon: <User className="h-3.5 w-3.5" />,
                  },
                  partner: {
                    border:
                      'border-sky-300 dark:border-sky-800',
                    stripe: 'bg-sky-500/70',
                    header:
                      'bg-sky-50 text-sky-800 border-b border-sky-200 dark:bg-sky-950/30 dark:text-sky-200 dark:border-sky-900/40',
                    label: 'Partner Entry',
                    icon: <UserCheck className="h-3.5 w-3.5" />,
                  },
                  buddy_member: {
                    border:
                      'border-violet-300 dark:border-violet-800',
                    stripe: 'bg-violet-500/70',
                    header:
                      'bg-violet-50 text-violet-800 border-b border-violet-200 dark:bg-violet-950/30 dark:text-violet-200 dark:border-violet-900/40',
                    label: 'Buddy Group Member',
                    icon: <Users className="h-3.5 w-3.5" />,
                  },
                }[entryType];
                // Whether to show the amber "side entry" banner on
                // this card. Only meaningful when the viewer actually
                // has a buddy group at this rodeo — otherwise calling
                // every solo entry a "side entry" would be misleading.
                const showSideEntryBanner =
                  !inBuddyGroup &&
                  userInBuddyGroupAtRodeo &&
                  !isManagedView;
                return (
                  <div key={reg.id} className="space-y-2">
                    {/* Card uses an entry-type-driven colour family
                        (mine = primary, partner = sky, buddy_member =
                        violet) plus a coloured top header strip that
                        names the row owner, so multi-row views (your
                        Header + your partner's Heeler, or a primary's
                        view of every buddy-group member) read as
                        clearly different entries instead of blending. */}
                    <div
                      className={
                        'relative rounded-xl border-2 bg-card overflow-hidden ' +
                        entryStyle.border
                      }
                    >
                      <span
                        aria-hidden
                        className={
                          'absolute left-0 top-0 bottom-0 w-1 ' +
                          entryStyle.stripe
                        }
                      />
                      {/* Coloured header strip — clear ownership label
                          so Header (yours) vs Heeler (partner's) is
                          unambiguous at a glance. */}
                      <div
                        className={
                          'flex items-center gap-1.5 px-4 pl-5 py-1.5 text-[11px] font-bold uppercase tracking-wider ' +
                          entryStyle.header
                        }
                      >
                        {entryStyle.icon}
                        <span>{entryStyle.label}</span>
                        {memberName && entryType !== 'mine' && (
                          <>
                            <span className="opacity-50">—</span>
                            <span className="font-semibold normal-case tracking-normal">
                              {memberName}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="p-4 pl-5 space-y-3">
                      {/* Buddy-group / side-entry indicator, lives
                          INSIDE the card so the per-card status is
                          obvious for both my row and partner's row.
                          Violet = tied to a buddy group; amber = side
                          entry (a row outside the buddy group when the
                          viewer has a group at this rodeo). */}
                      {inBuddyGroup ? (
                        <div className="flex items-start gap-2 rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100">
                          <Users className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            <div>
                              {isManagedView && memberName ? (
                                <>
                                  <span className="font-semibold">
                                    {memberName}
                                  </span>
                                  &apos;s entry —{' '}
                                </>
                              ) : (
                                <>This entry is </>
                              )}
                              part of buddy group{' '}
                              <span className="font-semibold">
                                {groupName ?? 'yours'}
                              </span>
                              .
                            </div>
                            {!isManagedView && (
                              <div className="text-[11px] text-violet-700/90 dark:text-violet-300/90">
                                Cancelling here only cancels this event
                                entry — you stay in the group. To leave
                                the group entirely,{' '}
                                <Link
                                  to={`/my/buddy-groups/${reg.buddy_group_id}`}
                                  className="underline underline-offset-2 hover:text-violet-950 dark:hover:text-violet-50"
                                >
                                  visit the buddy group page
                                </Link>
                                .
                              </div>
                            )}
                          </div>
                        </div>
                      ) : showSideEntryBanner ? (
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <div>
                            Side entry — not tied to buddy group{' '}
                            <span className="font-semibold">
                              {userBuddyGroupName ?? 'yours'}
                            </span>
                            . This entry will be drawn independently of the
                            buddy block.
                          </div>
                        </div>
                      ) : null}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          {reg.position && (
                            <Badge variant="outline" className="font-medium">
                              {reg.position}
                            </Badge>
                          )}
                          <StatusBadge status={regStatus} variant="registration" />
                          {/* Bank (ACH) verification pending: the debit hasn't
                              started — the user must verify microdeposits within
                              Stripe's window. The "Verify bank" action sits in
                              the row actions; this badge explains the hold. */}
                          {(reg as any).ach_verification_pending &&
                            regStatus !== 'cancelled' && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-900/40"
                              >
                                Verifying bank
                              </Badge>
                            )}
                          {/* Bank (ACH) debit submitted and clearing (~3–5
                              business days). No action needed — the spot is
                              held; Pay Now is suppressed so a card charge can't
                              land on top of the clearing debit. */}
                          {(reg as any).ach_processing &&
                            regStatus !== 'cancelled' && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-900/40"
                              >
                                Bank payment processing
                              </Badge>
                            )}
                          {/* Payment-state badge. Shows "Paid by <name>"
                              when the payer differs from the row owner
                              (paid_for_partner case — payment_payer_* is
                              enriched server-side only when payer ≠
                              owner), or a plain "Paid" when the user
                              settled their own row. Skipped when the row
                              has no completed payment behind it. */}
                          {/* Suppress the "Paid" badge when this row has
                              a supplemental balance still owed (post-
                              rejection surcharge). The original capture
                              landed but the registration isn't fully
                              settled — showing "Paid" alongside a Pay
                              Now button is misleading. */}
                          {/* Per-row paid amount. Shown in the badges
                              row so the user can tell two same-event,
                              same-position registrations apart by what
                              they actually paid — critical when picking
                              which one to cancel (e.g. a $130 non-member
                              row vs a $105 member row on the same event).
                              `payment_amount` is the right source: the
                              column tracks the captured-equivalent total
                              for confirmed rows (and pending balance for
                              pending_payment rows; we only render this
                              on the "Paid" branch below).
                              For paid_for_partner rows the stored value
                              is per-side, which is also exactly what the
                              user paid for that seat. */}
                          {(() => {
                            const paidAmount = Number(
                              (reg as any).payment_amount,
                            );
                            const showPaid =
                              (reg as any).payment_status === 'completed' &&
                              regStatus !== 'cancelled' &&
                              (owedByRegistrationId.get(reg.id) ?? 0) <= 0 &&
                              Number.isFinite(paidAmount) &&
                              paidAmount > 0;
                            const payerName = (reg as any)
                              .payment_payer_display_name;
                            if (showPaid && payerName) {
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs font-normal bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40"
                                >
                                  Paid {formatCurrency(paidAmount)} by {payerName}
                                </Badge>
                              );
                            }
                            if (showPaid) {
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs font-normal bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40"
                                >
                                  Paid {formatCurrency(paidAmount)}
                                </Badge>
                              );
                            }
                            // Free / no-fee rows still get the simple
                            // "Paid" tag so the visual cadence stays
                            // consistent across the list.
                            if (
                              (reg as any).payment_status === 'completed' &&
                              regStatus !== 'cancelled' &&
                              (owedByRegistrationId.get(reg.id) ?? 0) <= 0 &&
                              !payerName
                            ) {
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs font-normal bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40"
                                >
                                  Paid
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                          {/* Supplemental-balance badge so it's obvious
                              why Pay Now is showing on a "Confirmed" row. */}
                          {regStatus !== 'cancelled' &&
                            (owedByRegistrationId.get(reg.id) ?? 0) > 0 && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/40"
                              >
                                Balance Due
                              </Badge>
                            )}
                          {reg.created_at && (
                            <span className="text-xs text-muted-foreground">
                              {formatDate(reg.created_at)}
                            </span>
                          )}
                          {reg.registration_type && reg.registration_type !== 'individual' && (
                            <span className="text-xs text-muted-foreground capitalize">
                              {reg.registration_type.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Approve / Reject for partner pairings now
                              live inside the pre-approval banner below
                              (where the explanatory copy is) so the
                              "Approve below" call-to-action actually
                              points at the buttons, not above them. */}
                          {(regStatus === 'pending_payment' ||
                            // Confirmed-with-supplemental: e.g. the
                            // membership claim was rejected after the
                            // user already paid the entry fee, leaving
                            // a partial balance on a row that's still
                            // 'confirmed'. Show Pay Now so they can
                            // settle the diff via the supplemental
                            // PayPal flow.
                            (regStatus === 'confirmed' &&
                              (owedByRegistrationId.get(reg.id) ?? 0) > 0)) &&
                            // Suppress on partnerless team-roping rows
                            // (same gate as PaymentsTab/UpcomingEventsSection):
                            // these rows will be cancelled at draw time, so
                            // collecting payment would just create a refund.
                            // partner_status catches the explicit
                            // needs_partner flag; draw_eligible catches the
                            // other shapes (none/cancelled/rejected on a
                            // team-event row) for entries whose
                            // partner_status wasn't migrated.
                            (reg as any).partner_status !== 'needs_partner' &&
                            (reg as any).draw_eligible !== false &&
                            // Suppress Pay Now while a bank (ACH) debit is
                            // already CLEARING (processing): it can't be
                            // cancelled, so a competing card charge would
                            // double-charge if the debit later succeeds. The
                            // "Bank payment processing" note below explains the
                            // state. (Verifying ACH is different — it stays
                            // payable behind the supersede warning.)
                            !(reg as any).ach_processing && (
                              <>
                                {/* Mid-ACH-verification: surface "Verify bank"
                                    as the primary action (opens Stripe's hosted
                                    verification page) so the user finishes the
                                    bank check rather than starting a new
                                    payment that abandons it. */}
                                {(reg as any).ach_verification_pending &&
                                  (reg as any).ach_verification_url && (
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() =>
                                        window.open(
                                          (reg as any).ach_verification_url,
                                          '_blank',
                                          'noopener,noreferrer',
                                        )
                                      }
                                    >
                                      Verify bank
                                    </Button>
                                  )}
                                <Button
                                  variant={
                                    (reg as any).ach_verification_pending
                                      ? 'outline'
                                      : 'destructive'
                                  }
                                  size="sm"
                                  className={
                                    (reg as any).ach_verification_pending
                                      ? ''
                                      : 'animate-pulse'
                                  }
                                  onClick={() =>
                                    handlePayNowClick(
                                      reg,
                                      getEventDisplayTitle(ev),
                                    )
                                  }
                                >
                                  Pay Now
                                </Button>
                              </>
                            )}
                          {regStatus !== 'cancelled' && !entriesHaveClosed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => handleCancelClick(reg.id)}
                              disabled={actionLoading === reg.id}
                            >
                              {actionLoading === reg.id ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Partner info */}
                      {(reg.registration_type === 'team_primary' ||
                        reg.registration_type === 'team_partner') && (
                        <PartnerInfoDisplay
                          registration={reg}
                          eventId={ev?.id ?? null}
                          eventName={getEventDisplayTitle(ev)}
                          entriesHaveClosed={entriesHaveClosed}
                        />
                      )}

                      {/* Performance preferences — 1st / 2nd choice plus
                          the priority-resolved slot when it differs (e.g.
                          the resolver moved the user out of an
                          oversubscribed performance into their 2nd
                          choice). Hidden entirely on rows with no
                          preference data so non-preference events stay
                          visually quiet. The Edit affordance is gated
                          on (a) the row being owned by the viewer
                          (managed-view = read-only) AND (b) it not
                          being a buddy-group row (locked at the group
                          level) AND (c) entries not having closed yet —
                          all three checks mirror the backend gates. */}
                      {!isFinalsRodeo && (
                      <div className="space-y-1.5">
                        <EntryPreferencesDisplay
                          entry={reg as EntryWithPreferences}
                          variant="detailed"
                        />
                        {!reg.is_primary_managed_view &&
                          !reg.buddy_group_id &&
                          !entriesHaveClosed && (
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() =>
                                  setEditPrefsTarget({
                                    registrationId: reg.id,
                                    currentFirst:
                                      (reg.first_performance_preference as
                                        | string
                                        | null) ?? null,
                                    currentSecond:
                                      (reg.second_performance_preference as
                                        | string
                                        | null) ?? null,
                                  })
                                }
                              >
                                Edit preferences
                              </Button>
                            </div>
                          )}
                      </div>
                      )}

                      {/* Pre-approval payment-posture notice + action
                          buttons for the partner. The notice tells them
                          whether the primary is already covering the
                          entry fee (free seat) or whether they'll owe
                          their share after approving; Approve / Reject
                          live in the same block so the call-to-action is
                          adjacent to the buttons it points at.
                          Detection priority:
                          1. `payment_payer_display_name` set → linked
                             payment was captured by someone else (primary
                             paid for me via PayPal, immediate flow). Has
                             to be checked first because the bulk fan-out
                             auto-promotes the partner row to 'confirmed'
                             on capture, so regStatus alone misses this case.
                          2. `regStatus === 'pending_confirmation'` →
                             primary opted to cover but hasn't paid yet
                             (Pay Later flow).
                          3. otherwise → partner pays their own share. */}
                      {reg.registration_type === 'team_partner' &&
                        reg.partner_status === 'pending' &&
                        !reg.is_primary_managed_view && (() => {
                          const partnerCovered =
                            !!(reg as any).payment_payer_display_name ||
                            regStatus === 'pending_confirmation';
                          const alreadyPaid =
                            (reg as any).payment_status === 'completed';
                          return (
                          <div
                            className={
                              partnerCovered
                                ? 'rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 p-3 space-y-3'
                                : 'rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-3'
                            }
                          >
                            <div className="flex items-start gap-2 text-xs">
                              <AlertCircle
                                className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                                  partnerCovered
                                    ? 'text-emerald-700 dark:text-emerald-400'
                                    : 'text-amber-700 dark:text-amber-400'
                                }`}
                              />
                              <div className="space-y-0.5">
                                {partnerCovered ? (
                                  <>
                                    <p className="font-medium text-emerald-800 dark:text-emerald-200">
                                      {alreadyPaid
                                        ? 'Your partner already paid your entry fee'
                                        : 'Your partner is paying your entry fee'}
                                    </p>
                                    <p className="text-emerald-700/80 dark:text-emerald-300/80">
                                      {reg.partner_name
                                        ? `${reg.partner_name} has opted to cover this entry. `
                                        : 'Your partner has opted to cover this entry. '}
                                      Approve below to lock in your seat — no payment is required from you.
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-medium text-amber-800 dark:text-amber-200">
                                      You'll need to pay your share after approving
                                    </p>
                                    <p className="text-amber-700/80 dark:text-amber-300/80">
                                      Your partner has invited you to team up. After you approve, your row will move to{' '}
                                      <span className="font-medium">Pending Payment</span>{' '}
                                      so you can settle your own entry fee.
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive"
                                onClick={() =>
                                  handlePartnerRespond(reg.id, false)
                                }
                                disabled={actionLoading === reg.id}
                              >
                                Reject
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() =>
                                  handlePartnerRespond(reg.id, true)
                                }
                                disabled={actionLoading === reg.id}
                              >
                                {actionLoading === reg.id
                                  ? 'Approving...'
                                  : 'Approve'}
                              </Button>
                            </div>
                          </div>
                          );
                        })()}

                      {/* Payment pending breakdown — uses the registration's
                          stored non_member_fee (accurate to what was charged
                          when the row was created, even if policy/membership
                          state has since changed) and doubles every line for
                          paid_for_partner so the total matches what's actually
                          due. When the primary cancels their partner-pay (or
                          the partner withdraws) the backend flips
                          paid_for_partner=false and resets payment_amount, so
                          the next render naturally drops back to single
                          values. Mirrors the cancel refund estimate logic. */}
                      {regStatus === 'pending_payment' && (() => {
                        // paid_for_partner rows always display the doubled
                        // per-side total for entry + transaction — that's
                        // the amount actually charged to PayPal. Non-member
                        // surcharge is asymmetric: only the side(s) without
                        // membership contribute, so the backend ships
                        // partner_non_member_fee separately and we sum
                        // primary + partner instead of doubling primary.
                        const paidForPartner = !!reg.paid_for_partner;
                        const multiplier = paidForPartner ? 2 : 1;
                        const partnerSuffix = paidForPartner ? ' × 2' : '';
                        // Prefer the entry's own resolved fee (admin waives live
                        // on the registration, not the event).
                        const perSideEntryFee =
                          (reg as any).event_entry_fee != null
                            ? Number((reg as any).event_entry_fee) || 0
                            : Number(ev.entry_fee) || 0;
                        const entryFee = Math.max(0, perSideEntryFee) * multiplier;
                        const primaryNonMember = Math.max(0, Number(reg.non_member_fee) || 0);
                        // While a bank (ACH) verification is in progress the
                        // contestant is paying by bank, which waives the
                        // transaction fee — show the ACH price (e.g. $150), not
                        // the card-priced total. Reverts to the card fee once
                        // the verification expires/denies (ach_verification_pending
                        // drops) and the row falls back to the Pay-Now card flow.
                        const transactionFee = (reg as any).ach_verification_pending
                          ? 0
                          : perRegEffectiveTxnFee(primaryNonMember, reg) * multiplier;
                        const partnerNonMember =
                          paidForPartner && (reg as any).partner_non_member_fee != null
                            ? Math.max(0, Number((reg as any).partner_non_member_fee) || 0)
                            : null;
                        let nonMemberFeeLine: number;
                        let nonMemberSuffix: string;
                        if (paidForPartner) {
                          if (partnerNonMember != null) {
                            nonMemberFeeLine = primaryNonMember + partnerNonMember;
                            // "× 2" only when BOTH sides have the surcharge —
                            // otherwise it falsely implies $X each.
                            nonMemberSuffix =
                              primaryNonMember > 0 && partnerNonMember > 0 ? ' × 2' : '';
                          } else {
                            // Legacy fallback when partner field absent.
                            nonMemberFeeLine = primaryNonMember * 2;
                            nonMemberSuffix = primaryNonMember > 0 ? ' × 2' : '';
                          }
                        } else {
                          nonMemberFeeLine = primaryNonMember;
                          nonMemberSuffix = '';
                        }
                        const breakdownTotal = entryFee + transactionFee + nonMemberFeeLine;
                        return (
                          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-1.5">
                            <p className="font-medium text-destructive text-xs flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Payment Required
                            </p>
                            {paidForPartner && (
                              <p className="text-[11px] inline-flex items-center gap-1 rounded bg-primary/10 text-primary px-1.5 py-0.5 font-medium w-fit">
                                Paying for partner — fees doubled
                              </p>
                            )}
                            {entryFee > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Entry fee{partnerSuffix}</span>
                                <span>{formatCurrency(entryFee)}</span>
                              </div>
                            )}
                            {transactionFee > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Transaction fee{partnerSuffix}</span>
                                <span>{formatCurrency(transactionFee)}</span>
                              </div>
                            )}
                            {nonMemberFeeLine > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Non-member fee{nonMemberSuffix}</span>
                                <span>{formatCurrency(nonMemberFeeLine)}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-medium border-t border-destructive/20 pt-1.5 text-xs">
                              <span>Total</span>
                              <span>{formatCurrency(breakdownTotal)}</span>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Supplemental payment-required panel — the row's
                          entry + transaction fee were already captured; a
                          balance was added later (typically the post-
                          membership-rejection non-member surcharge plus the
                          Stripe fee on that supplemental capture). Show the
                          FULL picture so it's clear what the registration
                          costs, what's already paid, and what's still due.
                          The transaction fee is derived from the row's total
                          (payment_amount) so the lines always reconcile. */}
                      {regStatus === 'confirmed' &&
                        (owedByRegistrationId.get(reg.id) ?? 0) > 0 &&
                        (() => {
                          const dueNow = owedByRegistrationId.get(reg.id) ?? 0;
                          const rowTotal = Math.max(0, Number(reg.payment_amount) || 0);
                          const entryFee = Math.max(0, Number(ev.entry_fee) || 0);
                          const surcharge = Math.max(0, Number(reg.non_member_fee) || 0);
                          const txnFee = Math.max(
                            0,
                            Math.round((rowTotal - entryFee - surcharge) * 100) / 100,
                          );
                          const alreadyPaid = Math.max(
                            0,
                            Math.round((rowTotal - dueNow) * 100) / 100,
                          );
                          return (
                            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-1.5">
                              <p className="font-medium text-destructive text-xs flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Payment Required
                              </p>
                              <p className="text-[11px] text-muted-foreground">
                                You've already paid the entry fee and transaction
                                fee. The balance below was added after your
                                membership claim was rejected — pay it to settle
                                this registration.
                              </p>
                              {entryFee > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Entry fee</span>
                                  <span>{formatCurrency(entryFee)}</span>
                                </div>
                              )}
                              {surcharge > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Non-member surcharge</span>
                                  <span>{formatCurrency(surcharge)}</span>
                                </div>
                              )}
                              {txnFee > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    Transaction fee{' '}
                                    <span className="text-[10px]">(non-refundable)</span>
                                  </span>
                                  <span>{formatCurrency(txnFee)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-xs border-t border-destructive/20 pt-1.5">
                                <span className="text-muted-foreground">Total</span>
                                <span>{formatCurrency(rowTotal)}</span>
                              </div>
                              {alreadyPaid > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Already paid</span>
                                  <span className="text-muted-foreground">
                                    −{formatCurrency(alreadyPaid)}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium border-t border-destructive/20 pt-1.5 text-xs">
                                <span>Total due now</span>
                                <span>{formatCurrency(dueNow)}</span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
                  </div>
                );
              })}
            </div>
            ) : null;
          })()}

          {/* Two gates that hide every register / re-register CTA:
                1. Rodeo already concluded (end_date past) — uses end-of-day
                   so a rodeo whose end_date is today still counts as
                   in-progress. Falls back to ev.trig_rodeo_end_date for
                   legacy event rows where parentJackpot hasn't loaded yet.
                2. Entries closed (entry_close_time past) — uses the
                   entriesHaveClosed memo computed above, which is also the
                   server-side gate for createRegistration. Past this point
                   users have to contact the org out-of-band. */}
          {(() => {
            const endRaw =
              (parentJackpot as any)?.end_date ??
              (ev as any)?.trig_rodeo_end_date ??
              null;
            if (!endRaw) return false;
            const endMs = new Date(`${endRaw}T23:59:59`).getTime();
            return Number.isFinite(endMs) && endMs < Date.now();
          })() ? (
            <div className="flex items-center justify-center">
              <Badge
                variant="outline"
                className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
              >
                This event has already taken place — registrations are closed.
              </Badge>
            </div>
          ) : entriesHaveClosed ? (
            <div className="flex items-center justify-center">
              <Badge
                variant="outline"
                className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
              >
                Registrations are closed for this event. Contact the organization if you need to be added.
              </Badge>
            </div>
          ) : isAuthenticated ? (
            <div className="flex flex-col items-center justify-center gap-2">
              {(() => {
                // Resolve the per-event multi-registration cap. When the org
                // has opted in, the contestant may hold up to N active
                // entries; otherwise we fall back to the legacy single-row
                // (or one-per-position for team roping) behaviour.
                const allowMultiReg = !!ev.allow_multiple_registrations;
                const maxPerUser = Math.max(
                  1,
                  Number(ev.max_registrations_per_user ?? 1) || 1,
                );
                const multiRegEnabled = allowMultiReg && maxPerUser > 1;
                const activeCount = ownActiveRegistrations.length;
                // Header + Heeler share one budget under the team-
                // roping total-entries cap, so the at-cap gate uses
                // the total `activeCount` instead of inspecting each
                // side independently. The position-level breakdown is
                // still surfaced inside the registration modal via
                // `actorPositionCounts` for the picker hint copy.

                // Single-button label resolver: when the viewer is a
                // buddy-group primary the modal's target picker covers
                // both self and on-behalf flows, so we collapse the two
                // CTAs into one labeled "Register Yourself or Buddy
                // Group Member" (or just "Register Buddy Group Member"
                // when self is exhausted). For non-primaries the label
                // stays the original self-only copy.
                //
                // Team roping uses one total budget across Header and
                // Heeler — 1 entry means a single row in either side
                // (not both), 2 entries means up to 2 rows in any
                // role/partner combination. Solo events follow the
                // same total-cap rule.
                const selfStillAvailable = (() => {
                  if (multiRegEnabled) {
                    return activeCount < maxPerUser;
                  }
                  return activeCount === 0;
                })();
                const selfLabelDefault = (() => {
                  if (activeCount === 0) {
                    return capacity?.is_full
                      ? 'Join Waitlist'
                      : 'Enter Now For This Event';
                  }
                  if (multiRegEnabled && selfStillAvailable) {
                    return 'Enter Again';
                  }
                  return null;
                })();
                // Buddy-group primaries lose the on-behalf CTA when
                // every registerable member (including self) has hit
                // the cap — otherwise the button is a dead-end into a
                // modal that can't submit. The "Enter Yourself" path
                // also implicitly requires self to have slots left,
                // which is already covered by the selfStillAvailable
                // branch above.
                const buttonLabel = isBuddyGroupPrimary
                  ? selfStillAvailable
                    ? 'Enter Yourself or Buddy Group Member'
                    : allBuddyGroupMembersAtCap
                      ? null
                      : 'Enter Buddy Group Member'
                  : selfLabelDefault;

                // No button to render — surface a badge so the
                // contestant understands why no CTA is showing instead
                // of leaving the entry section silently empty. Three
                // shapes: buddy-group primary with every member at cap
                // ("Everyone in your buddy group …"), multi-reg cap
                // exhausted ("You've used all N entries"), and single-
                // entry already in ("You're already entered").
                if (!buttonLabel) {
                  if (isBuddyGroupPrimary && allBuddyGroupMembersAtCap) {
                    if (multiRegEnabled) {
                      return (
                        <Badge
                          variant="outline"
                          className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
                        >
                          {`Everyone in your buddy group has used all ${maxPerUser} entries for this event`}
                        </Badge>
                      );
                    }
                    return (
                      <Badge
                        variant="outline"
                        className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
                      >
                        Everyone in your buddy group is already entered in this event
                      </Badge>
                    );
                  }
                  if (!selfStillAvailable && !isBuddyGroupPrimary) {
                    if (multiRegEnabled) {
                      return (
                        <>
                          <Badge
                            variant="outline"
                            className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
                          >
                            {`You've used all ${maxPerUser} entries for this event`}
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            {`Entered ${activeCount} of ${maxPerUser} times`}
                          </p>
                        </>
                      );
                    }
                    return (
                      <Badge
                        variant="outline"
                        className="bg-muted text-muted-foreground border-border px-3 py-1.5 text-sm"
                      >
                        You're already entered in this event
                      </Badge>
                    );
                  }
                  return null;
                }

                // Caption under the button — shows the running count for
                // multi-reg events, plus an on-behalf hint when the
                // primary's own quota is gone. Team roping and solo
                // share one total cap so the caption is the same
                // "X of Y" string for both.
                const captionText = (() => {
                  if (multiRegEnabled && activeCount > 0) {
                    if (!selfStillAvailable) {
                      return isBuddyGroupPrimary
                        ? `You've used all ${maxPerUser} of your own entries. Pick a teammate to sign them up on behalf.`
                        : null;
                    }
                    return `Entered ${activeCount} of ${maxPerUser} times`;
                  }
                  if (
                    !selfStillAvailable &&
                    isBuddyGroupPrimary &&
                    activeCount > 0
                  ) {
                    return "You're entered for this event. Pick a buddy group teammate to sign them up on behalf.";
                  }
                  return null;
                })();

                // The button uses outline styling when the primary self
                // path is exhausted (it's now an on-behalf-only action),
                // otherwise the prominent solid CTA.
                const buttonVariant =
                  isBuddyGroupPrimary && !selfStillAvailable
                    ? 'outline'
                    : 'default';

                return (
                  <>
                    <Button
                      onClick={() => setShowRegistrationModal(true)}
                      size="lg"
                      variant={buttonVariant}
                    >
                      {buttonLabel}
                    </Button>
                    {captionText && (
                      <p className="text-xs text-muted-foreground">
                        {captionText}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <Button variant="outline" onClick={() => navigate('/login')}>
              Login to Enter
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Registration modal */}
      <RegistrationModal
        open={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        eventId={eventId!}
        // Merge `rodeo_or_jackpot` from the parent rodeo onto the event prop
        // so the wizard can drop the handicap step for rodeo events.
        // Handicaps are jackpot-only.
        event={{ ...ev, rodeo_or_jackpot: parentJackpot?.rodeo_or_jackpot ?? null }}
        registeredPositions={registeredPositions}
        // Hand the modal the viewer's own active count so it can block
        // self-selection in the buddy-group target picker once the cap
        // is hit. Backend re-checks at submit; this is purely UX.
        actorActiveRegistrationCount={ownActiveRegistrations.length}
        // Per-position breakdown for the position-picker hint copy
        // ("1 entered" under a Header/Heeler button). The cap gate
        // itself runs off the total active count because Header and
        // Heeler share one budget.
        actorPositionCounts={ownPositionCounts}
        onSuccess={() => {
          setShowRegistrationModal(false);
        }}
      />

      {/* Cancel registration confirmation dialog */}
      <CancelRegistrationDialog
        open={pendingCancelId !== null}
        onClose={() => setPendingCancelId(null)}
        onConfirm={handleCancelConfirm}
        loading={actionLoading !== null}
        refundEstimate={cancelRefundEstimate}
      />

      {/* Edit performance preferences for an individual (non-buddy-
          group) entry. Buddy-group rows are filtered out of the
          affordance above so the dialog should never be opened against
          one — backend also rejects with 409 BUDDY_GROUP_LOCKED. */}
      <EditPreferencesDialog
        open={editPrefsTarget !== null}
        onClose={() => setEditPrefsTarget(null)}
        registrationId={editPrefsTarget?.registrationId ?? ''}
        eventId={eventId ?? ''}
        rodeoId={rodeoId ?? null}
        currentFirst={editPrefsTarget?.currentFirst ?? null}
        currentSecond={editPrefsTarget?.currentSecond ?? null}
        eventName={getEventDisplayTitle(ev)}
      />

      {/* Pay Now (PayPal) dialog for existing pending_payment registrations */}
      <PayNowDialog
        open={payNowRegistrationId !== null}
        onClose={() => setPayNowRegistrationId(null)}
        registrationId={payNowRegistrationId ?? ''}
        eventName={getEventDisplayTitle(ev)}
        fallbackTotal={totalFee}
        transactionFeeTotal={
          (() => {
            const reg = ownActiveRegistrations.find(
              (r: any) => r.id === payNowRegistrationId,
            ) as any;
            const txn = perRegEffectiveTxnFee(Math.max(0, Number(reg?.non_member_fee) || 0));
            // paid_for_partner rows always reflect 2× the per-side txn
            // fee (one per seat) since the user paid for both halves.
            return reg?.paid_for_partner ? txn * 2 : txn;
          })()
        }
        onSuccess={() => setPayNowRegistrationId(null)}
      />

      {/* Bulk pay dialog — fired when the deep-link `?action=pay` finds
          MULTIPLE pending_payment registrations the user owns on this
          event. One Stripe PaymentIntent settles every row at once, so
          the total reflects everything they owe for the event (e.g.
          $210 buddy-group team_primary + $105 solo = $315) instead of
          just the first row's amount. */}
      <BulkPayDialog
        open={bulkPayIds !== null}
        onClose={() => setBulkPayIds(null)}
        registrationIds={bulkPayIds ?? []}
        fallbackTotal={bulkPayTotal}
        transactionFeeTotal={bulkPayTransactionFee}
        onSuccess={() => setBulkPayIds(null)}
      />

      {/* ACH-verification warning: clicking Pay Now on an entry whose current
          payment is a bank debit still awaiting microdeposit verification would
          supersede that pending ACH (the backend cancels the unverified
          PaymentIntent on completion). Make the trade-off explicit so the user
          doesn't abandon a verification in progress by accident. */}
      <Dialog
        open={verifyWarn !== null}
        onOpenChange={(next) => {
          if (!next) setVerifyWarn(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle>Bank verification still pending</DialogTitle>
            </div>
            <DialogDescription>
              {verifyWarn?.eventName
                ? `This entry (${verifyWarn.eventName}) has a bank `
                : 'This entry has a bank '}
              (ACH) payment that's still waiting for microdeposit verification.
              If you continue with a different payment method, that pending ACH
              will be cancelled so you aren't charged twice. You can also close
              this and finish verifying your bank instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setVerifyWarn(null)}>
              Keep waiting
            </Button>
            <Button
              onClick={() => {
                const id = verifyWarn?.id ?? null;
                setVerifyWarn(null);
                if (id) setPayNowRegistrationId(id);
              }}
            >
              Continue with new payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Stat Card Component ──────────────────────────────── */
function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold tracking-tight text-center">{value}</p>
    </div>
  );
}

/* ─── Detail Row Component ──────────────────────────────── */
function DetailRow({
  label,
  value,
  bold,
  capitalize: cap,
  hint,
}: {
  label: string;
  value: string | number | null | undefined;
  bold?: boolean;
  capitalize?: boolean;
  /** Optional muted subtext rendered under the row — used for things like
   *  "Waived when paying via bank transfer (ACH)" on the transaction fee. */
  hint?: string;
}) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={`${bold ? 'font-semibold' : 'font-medium'} ${cap ? 'capitalize' : ''}`}>
          {value}
        </span>
      </div>
      {hint && (
        <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
      )}
    </div>
  );
}
